import { periodRange, STATUSES_BY_FILTER, toHistoryRow } from '../api/history';
import type { PdsApiOrder } from '../api/types';
import type { HistoryPage, HistoryQuery, HistorySort } from '../history';
import { buildFakeHistory } from './history.seed';

const PAYMENT_STATUS = {
  paid: 'paid',
  pending: 'payment_pending',
  refunded: 'refunded',
} as const;

const itemsOf = (order: PdsApiOrder) =>
  (order.items ?? []).reduce((sum, item) => sum + item.qty, 0);
const timeOf = (value?: string | null) => (value ? Date.parse(value) : 0);

const SORTERS: Record<HistorySort, (a: PdsApiOrder, b: PdsApiOrder) => number> =
  {
    newer: (a, b) => timeOf(b.orderDate) - timeOf(a.orderDate),
    older: (a, b) => timeOf(a.orderDate) - timeOf(b.orderDate),
    due_asc: (a, b) => timeOf(a.pickupTime) - timeOf(b.pickupTime),
    due_desc: (a, b) => timeOf(b.pickupTime) - timeOf(a.pickupTime),
    price_d: (a, b) => (b.totalPrice ?? 0) - (a.totalPrice ?? 0),
    price: (a, b) => (a.totalPrice ?? 0) - (b.totalPrice ?? 0),
    items_desc: (a, b) => itemsOf(b) - itemsOf(a),
    items_asc: (a, b) => itemsOf(a) - itemsOf(b),
  };

const squash = (text: string) => text.toLowerCase().replace(/\s+/g, '');

// Même sens que les filtres du serveur : plage de dates incluse, recherche partielle insensible à la casse.
export const searchFakeHistory = (
  query: HistoryQuery,
  now: number,
): HistoryPage => {
  const { from, to } = periodRange(query, now);
  const needle = squash(query.q.trim());

  const matching = buildFakeHistory(now)
    .filter((order) => {
      const orderedAt = timeOf(order.orderDate);
      const total = order.totalPrice ?? 0;
      return (
        (query.status === 'all' ||
          STATUSES_BY_FILTER[query.status].includes(order.status)) &&
        (query.payment === 'all' ||
          order.paymentStatus === PAYMENT_STATUS[query.payment]) &&
        (query.channel === 'all' || order.channel === query.channel) &&
        (!from || orderedAt >= from.getTime()) &&
        (!to || orderedAt <= to.getTime()) &&
        (query.minTotal === null || total >= query.minTotal) &&
        (query.maxTotal === null || total <= query.maxTotal) &&
        (!needle ||
          squash(
            [order.orderNumber, order.customerName, order.phone].join(''),
          ).includes(needle))
      );
    })
    .sort(SORTERS[query.sort]);

  const start = (query.page - 1) * query.pageSize;
  return {
    rows: matching.slice(start, start + query.pageSize).map(toHistoryRow),
    total: matching.length,
  };
};
